import { FC, Fragment } from 'react';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Icons
import { FaTrash } from 'react-icons/fa';
// Types
import QuizTypes from 'types/QuizTypes';
// Model
import IQuiz from 'models/Quiz';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Components
import { Button, IconButton } from 'components/Buttons';
import { Toggle } from 'components/Controls';
import { Tooltip } from 'components/Tooltip';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import Accordion from 'components/Accordion';
import { ColorPicker } from 'components/ColorPicker';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

const SectionPrizes:FC = () => {
  const { t } = useTranslation();

  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);

  const { control, watch, formState: { errors } } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prizes'
  });

  const handleAdd = () => {
    append({ prize: '' });
  }

  const handleRemove = (index:number) => () => {
    remove(index);
  }

  const spotPrizesEnabled = watch('spotPrizesEnabled');

  const isPollQuiz = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);
  const isSurveyQuiz = quiz?.type === QuizTypes.Survey;

  if ( quiz?.type === QuizTypes.Personality ) return null;
  return (
    <div react-scroll="prizes">
      <Accordion label={t('labels.prizes')} icon="cup">
        <div className="w-full">
          {!isPollQuiz && !isSurveyQuiz ? (
            <Fragment>
              <div className="mb-4 text-right">
                <Button
                  variant="outlined"
                  onClick={handleAdd}
                >{t('buttons.addPrize')}</Button>
              </div>
              {fields.map((field:any, index:number) => (
                <div key={`prize-item-${field.id}`} className="flex gap-2">
                  <div className="flex-grow">
                    <MultiLanguageTextField
                      name={`prizes.${index}.prize`}
                      rules={{ require: isRequired,validate: noWhitespace }}
                      label={`${t('labels.prize')} ${index + 1}`}
                      className="mb-4"
                      required
                      error={Boolean(errors.prizes?.[index]?.prize)}
                      errorText={errors.prizes?.[index]?.prize ? errors.prizes[index].prize.message : ''}
                    />
                  </div>
                  <div className="mt-6">
                    <Tooltip title={t('buttons.removePrize')}>
                      <IconButton
                        onClick={handleRemove(index)}
                      ><FaTrash /></IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
              <span className="divider my-8"></span>
            </Fragment>
          ) : null}
          <div className="flex items-center my-8">
            <span className="block text-white text-base font-semibold mr-3">{t('pages.quizForm.randomPrizeLabel')}</span>
            <Controller
              control={control} name="spotPrizesEnabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="spotPrizesEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <MultiLanguageTextField
            rules={{ required: spotPrizesEnabled ? isRequired : false, validate: spotPrizesEnabled ? noWhitespace : undefined }}
            name="spotPrizes"
            disabled={!spotPrizesEnabled}
            helperText={t('pages.quizForm.randomPrizeHint')}
            className="mb-4"
            required={spotPrizesEnabled}
            error={spotPrizesEnabled && Boolean(errors.spotPrizes)}
            errorText={spotPrizesEnabled && errors.spotPrizes ? errors.spotPrizes.message : ''}
          />
          {!isPollQuiz && !isSurveyQuiz ? (
            <Fragment>
              <h2 className="text-xl font-bold text-white mb-2">
                {t('pages.quizForm.prizesButtonGradientLabel')}
              </h2>
              <div className="flex flex-col items-center gap-4 md:flex-row">
                <div className="mb-4">
                  <Controller
                    control={control} name="prizesButtonGradientStart" rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.startColor')}
                        value={value}
                        required={true}
                        error={Boolean(errors.prizesButtonGradientStart)}
                        errorText={errors.prizesButtonGradientStart ? errors.prizesButtonGradientStart.message : ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="mb-4">
                  <Controller
                    control={control} name="prizesButtonGradientEnd" rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.endColor')}
                        value={value}
                        required={true}
                        error={Boolean(errors.prizesButtonGradientEnd)}
                        errorText={errors.prizesButtonGradientEnd ? errors.prizesButtonGradientEnd.message : ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="mb-4">
                  <Controller
                    control={control} name="prizesButtonFontColor" rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.textColor')}
                        value={value}
                        required={true}
                        error={Boolean(errors.prizesButtonFontColor)}
                        errorText={errors.prizesButtonFontColor ? errors.prizesButtonFontColor.message : ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </Fragment>
          ) : null}
        </div>
      </Accordion>
    </div>
  )
}

export default SectionPrizes;
